import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type MarketplaceStatus = 'NOT_STARTED' | 'PENDING' | 'AUTHORIZED' | 'ERROR' | 'EXPIRED';

export interface MarketplaceIntegrationConfig {
  status: MarketplaceStatus;
  collectorId?: string;
  sellerUserId?: string;
  sellerNickname?: string;
  sellerEmail?: string;
  applicationFeePercent?: number;
  fixedFeeAmount?: number;
  linkedAt?: string;
  lastAttemptAt?: string;
  lastError?: string;
  errorCount?: number;
  credentialMp?: {
    expiresAt?: string;
    scope?: string;
    lastRefreshAt?: string;
    lastError?: string;
  };
}

export interface InitiateMercadoPagoOAuthResponse {
  authorizationUrl: string;
  status: MarketplaceStatus;
}

export interface MercadoPagoOAuthCallbackResult {
  cnpj: string;
  redirectUrl?: string;
  seller?: {
    id: string;
    nickname?: string;
    email?: string;
  };
  status: MarketplaceStatus;
  tokens: {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
    scope?: string;
    userId?: string;
  };
}

export interface RefreshMercadoPagoOAuthResponse {
  cnpj: string;
  status: MarketplaceStatus;
  tokens: {
    accessToken: string;
    refreshToken?: string;
    expiresIn?: number;
    scope?: string;
    userId?: string;
  };
}

export interface ProcessCallbackResponse {
  success: boolean;
  status: string;
  sellerId?: string;
  redirectUrl?: string;
  message?: string;
}

class MercadoPagoOAuthService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 30000,
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async initiateOAuth(cnpj: string, redirectUrl?: string): Promise<InitiateMercadoPagoOAuthResponse> {
    try {
      const response: AxiosResponse<InitiateMercadoPagoOAuthResponse> = await this.client.post(
        '/admin/payments/mercadopago/oauth/start',
        { cnpj, redirectUrl }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao iniciar integração com Mercado Pago');
    }
  }

  async refreshToken(cnpj: string): Promise<RefreshMercadoPagoOAuthResponse> {
    try {
      const response: AxiosResponse<RefreshMercadoPagoOAuthResponse> = await this.client.post(
        '/admin/payments/mercadopago/oauth/refresh',
        { cnpj }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar credenciais do Mercado Pago');
    }
  }

  async processCallback(code: string | null, state: string | null): Promise<ProcessCallbackResponse> {
    try {
      if (!code || !state) {
        throw new Error('Parâmetros de autorização ausentes');
      }

      const response: AxiosResponse<ProcessCallbackResponse> = await this.client.post(
        '/admin/payments/mercadopago/oauth/callback',
        { code, state }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao processar callback do Mercado Pago');
    }
  }
}

export const mercadoPagoOAuthService = new MercadoPagoOAuthService();
export default mercadoPagoOAuthService;
